import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, CalendarDays, ArrowUpRight, CalendarPlus } from "lucide-react";
import PageHeader from "../components/PageHeader";
import api, { mediaUrl } from "../lib/api";

function pad(n) {
    return String(n).padStart(2, "0");
}
function toStamp(dateObj) {
    return `${dateObj.getUTCFullYear()}${pad(dateObj.getUTCMonth() + 1)}${pad(dateObj.getUTCDate())}`;
}
function esc(s = "") {
    return String(s).replace(/\\/g, "\\\\").replace(/[,;]/g, "\\$&").replace(/\n/g, "\\n");
}
function downloadCalendar(ev) {
    const start = new Date(ev.date + "T00:00:00Z");
    // Detect a day range (e.g. "May 13–16") to build a multi-day all-day event.
    const range = (ev.time || "").match(/(\d{1,2})\s*[–—-]\s*(\d{1,2})/);
    const end = new Date(start);
    if (range && Number(range[2]) > start.getUTCDate()) {
        end.setUTCDate(Number(range[2]) + 1); // DTEND is exclusive
    } else {
        end.setUTCDate(start.getUTCDate() + 1);
    }
    const now = new Date();
    const stampNow =
        `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}` +
        `T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
    const desc = [ev.description, ev.link ? `Register: ${ev.link}` : ""]
        .filter(Boolean)
        .join("\n\n");
    const lines = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//NeuroPlay Counseling//Events//EN",
        "CALSCALE:GREGORIAN",
        "BEGIN:VEVENT",
        `UID:${ev.id}@asafespacecounseling`,
        `DTSTAMP:${stampNow}`,
        `DTSTART;VALUE=DATE:${toStamp(start)}`,
        `DTEND;VALUE=DATE:${toStamp(end)}`,
        `SUMMARY:${esc(ev.title)}`,
        ev.location ? `LOCATION:${esc(ev.location)}` : "",
        desc ? `DESCRIPTION:${esc(desc)}` : "",
        ev.link ? `URL:${esc(ev.link)}` : "",
        "END:VEVENT",
        "END:VCALENDAR",
    ].filter(Boolean);
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ev.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Events() {
    const [events, setEvents] = useState(null);

    const load = useCallback(() => {
        api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const fmt = (d) =>
        new Date(d + "T00:00:00").toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    let content;
    if (events === null) {
        content = <p className="mt-10 text-sm text-amber-100/50">Loading…</p>;
    } else if (events.length === 0) {
        content = (
            <p
                data-testid="events-empty"
                className="mt-10 rounded-2xl border border-dashed border-amber-500/25 bg-[#1F1510]/50 p-8 text-center font-serif text-lg italic text-amber-200/70"
            >
                No upcoming events right now — check back soon.
            </p>
        );
    } else {
        content = (
            <div className="mt-10 grid gap-7 sm:grid-cols-2">
                {events.map((ev, i) => (
                    <motion.article
                        key={ev.id}
                        data-testid="event-card"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                        className="overflow-hidden rounded-2xl border border-amber-500/15 bg-[#1F1510]/50"
                    >
                        {ev.image_url && (
                            <div className="h-48 overflow-hidden">
                                <img
                                    src={mediaUrl(ev.image_url)}
                                    alt={ev.title}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        )}
                        <div className="p-7">
                            <h3 className="font-serif text-2xl text-amber-100">
                                {ev.title}
                            </h3>
                            <div className="mt-4 space-y-2 text-sm text-amber-100/70">
                                <p className="flex items-center gap-2">
                                    <CalendarDays className="h-4 w-4 text-amber-400" />
                                    {fmt(ev.date)}
                                </p>
                                {ev.time && (
                                    <p className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-amber-400" />
                                        {ev.time}
                                    </p>
                                )}
                                {ev.location && (
                                    <p className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-amber-400" />
                                        {ev.location}
                                    </p>
                                )}
                            </div>
                            {ev.description && (
                                <p className="mt-4 text-sm leading-relaxed text-amber-100/60">
                                    {ev.description}
                                </p>
                            )}
                            <div className="mt-5 flex flex-wrap items-center gap-3">
                                {ev.link && (
                                    <a
                                        data-testid="event-register-link"
                                        href={ev.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400"
                                    >
                                        Register now
                                        <ArrowUpRight className="h-4 w-4" />
                                    </a>
                                )}
                                <button
                                    data-testid="event-add-to-calendar"
                                    onClick={() => downloadCalendar(ev)}
                                    className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-5 py-2.5 text-sm font-semibold text-amber-200 transition-[background-color,transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-500/10"
                                >
                                    <CalendarPlus className="h-4 w-4" />
                                    Add to calendar
                                </button>
                            </div>
                        </div>
                    </motion.article>
                ))}
            </div>
        );
    }

    return (
        <div data-testid="events-page" className="pb-28">
            <PageHeader
                testid="events-header"
                breadcrumb="Events"
                title="Come play with us"
                number="◷"
                label="Gatherings"
                heading="Story times, parent nights & community days"
            >
                {content}
            </PageHeader>
        </div>
    );
}
